import { openUrl } from "@tauri-apps/plugin-opener";
import { Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import type { DictationAnywhereScreenRecording } from "@/desktop/dictation-anywhere/dictation-anywhere-native";
import { screenRecordingFileUrl } from "@/desktop/screen-recording-file-url";
import { formatNumber, useT } from "@/i18n";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";

interface VoiceRecentDictationRecordingsProps {
  historyId: string;
  recordings: readonly DictationAnywhereScreenRecording[];
  recordingFolder?: string;
  onError: (message: string | null) => void;
}

export function VoiceRecentDictationRecordings({
  historyId,
  recordings,
  recordingFolder,
  onError,
}: VoiceRecentDictationRecordingsProps) {
  const t = useT(settingsVoiceMessages);
  if (recordings.length === 0 && !recordingFolder) return null;

  async function open(path: string, folder = false) {
    onError(null);
    try {
      await openUrl(screenRecordingFileUrl(path));
    } catch {
      onError(t(folder ? "openFolderFailed" : "openRecordingFailed"));
    }
  }

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid={`voice-recent-dictation-recordings-${historyId}`}
    >
      {recordings.map((recording, index) => (
        <Button
          key={recording.clip_id}
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => void open(recording.original_path)}
          aria-label={t("playClip", { number: index + 1 })}
          title={recording.original_path}
          data-testid={`voice-recent-recording-play-${historyId}-${recording.clip_id}`}
        >
          <Icons.Play className="h-3 w-3" aria-hidden="true" />
          {t("clip", {
            number: index + 1,
            seconds: formatNumber(recording.duration_ms / 1_000, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            }),
          })}
        </Button>
      ))}
      {recordingFolder ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => void open(recordingFolder, true)}
          title={recordingFolder}
          data-testid={`voice-recent-recording-folder-${historyId}`}
        >
          {t("openRecordingFolder")}
        </Button>
      ) : null}
    </div>
  );
}
